import React, { useCallback, useEffect } from 'react';
import { useFocusEffect, useRouter } from 'expo-router';

import { OrderAgainRail } from '@order/features/orders';

import { useLastInteractedStore } from '../../context/LastInteractedStore';
import { ReorderOrderLineItem } from '../ReorderOrderLineItem';
import { useRecentOrders } from './hooks';
import { useRecentOrdersV2 } from './hooks/useRecentOrdersV2';

// ─────────────────────────────────────────────────────────────────────

/**
 * Renders user's most recent orders.
 *
 * NOTE: The component does not check for states like logged-in and instead
 *       relies on external render condition/s.
 */
export const RecentOrdersRail = (props: RecentOrderAgainRailProps) => {
  const { shouldRefetchOnFocus } = props;

  // Get last interacted store as fallback
  const lastInteractedStore = useLastInteractedStore();

  const currentRestaurantId = lastInteractedStore?.restaurantId
    ? Number.parseInt(lastInteractedStore.restaurantId, 10)
    : undefined;

  // ─── Remote Data ─────────────────────────────────────────────────────

  // Always call both hooks to follow React rules, then conditionally use the result

  const recentOrdersV1 = useRecentOrders();
  const recentOrdersV2 = useRecentOrdersV2(currentRestaurantId);

  const recentOrders = currentRestaurantId ? recentOrdersV2 : recentOrdersV1;

  const {
    fetchInitialOrders,
    refetchOrders,
    mostRecentLineItems,
    isFetchingInitialOrders,
    mostRecentOrder,
  } = recentOrders;

  // ─── Effects ─────────────────────────────────────────────────────────

  // Fetch orders on component mount
  useEffect(() => {
    fetchInitialOrders();
  }, [fetchInitialOrders]);

  // Refetch orders on screen focus
  useFocusEffect(
    useCallback(() => {
      if (!shouldRefetchOnFocus) return;

      refetchOrders();
    }, [refetchOrders, shouldRefetchOnFocus]),
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <RecentOrderAgainRailReady
      lineItems={mostRecentLineItems}
      mostRecentOrder={mostRecentOrder}
      isFetching={isFetchingInitialOrders}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────

export const RecentOrderAgainRailReady = (props: RecentOrderAgainRailReadyProps) => {
  const { lineItems, mostRecentOrder, isFetching } = props;

  const router = useRouter();

  // ─── Helpers ─────────────────────────────────────────────────────────

  const navigateToAllOrdersModal = useCallback(() => {
    router.navigate('/reorder');
  }, [router]);

  // ─────────────────────────────────────────────────────────────────────

  if (!isFetching && lineItems.length === 0) return null;

  return (
    <OrderAgainRail.Container>
      <OrderAgainRail.Header>
        <OrderAgainRail.Heading />
        <OrderAgainRail.ViewAllOrdersCTA onPress={navigateToAllOrdersModal} disabled={isFetching} />
      </OrderAgainRail.Header>

      {isFetching ? (
        <OrderAgainRail.LoadingPlaceholder />
      ) : (
        <OrderAgainRail.List>
          {lineItems.map((lineItem) => (
            <ReorderOrderLineItem
              key={lineItem.id}
              variation="vertical"
              lineItem={lineItem}
              orderDetails={lineItem.order}
              mostRecentOrder={mostRecentOrder}
              location="reorder"
              shouldShowSuccessBanner={true}
            />
          ))}
        </OrderAgainRail.List>
      )}
    </OrderAgainRail.Container>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type RecentOrderAgainRailProps = {
  shouldRefetchOnFocus?: boolean;
};

type RecentOrderAgainRailReadyProps = {
  lineItems: ReturnType<typeof useRecentOrders>['mostRecentLineItems'];
  mostRecentOrder: ReturnType<typeof useRecentOrders>['mostRecentOrder'];
  isFetching: boolean;
};
